import { OFREP_PROVIDER_NAME } from '@/app-shell/flags/flag-evaluation-telemetry';
import { getDomains } from '@/helpers/getDomains';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { getV2StreamHeaders } from '@/utils/http/scribeSseHeaders';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { OpenFeature } from '@openfeature/react-sdk';
import type { Hook } from '@openfeature/web-sdk';

// Sends keys of OFREP-served reads to ml-scribe so LaunchDarkly records client
// evaluations. Best effort: each key goes once per page load, then at most once
// per REPORT_INTERVAL_MS. Shadow reads are not reported.
export const EVALUATION_REPORTS_KILL_FLAG =
  'op-scribe-disable-launchdarkly-evaluation-reports';
export const REPORT_INTERVAL_MS = 30 * 60 * 1000;
export const FLUSH_DELAY_MS = 60_000;
export const MAX_KEYS_PER_REPORT = 200;
export const REQUEST_TIMEOUT_MS = 10_000;

const LAUNCHDARKLY_PROVIDER_NAME = 'launchdarkly';
const REPORTABLE_KEY = /^[A-Za-z0-9._-]{1,256}$/;

const pending = new Set<string>();
const lastSentAt = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let unloadListenersArmed = false;

const isServedByLaunchDarkly = (provider: unknown): boolean =>
  provider === OFREP_PROVIDER_NAME || provider === LAUNCHDARKLY_PROVIDER_NAME;

const isKilled = (): boolean =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    EVALUATION_REPORTS_KILL_FLAG,
    false
  );

const onVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden') flushLaunchDarklyEvaluationReports();
};

const armUnloadListeners = (): void => {
  if (unloadListenersArmed || typeof window === 'undefined') return;
  unloadListenersArmed = true;
  window.addEventListener('pagehide', flushLaunchDarklyEvaluationReports);
  document.addEventListener('visibilitychange', onVisibilityChange);
};

export const recordLaunchDarklyServedRead = (key: string): void => {
  if (pending.has(key) || !REPORTABLE_KEY.test(key)) return;
  const sentAt = lastSentAt.get(key);
  if (sentAt !== undefined && Date.now() - sentAt < REPORT_INTERVAL_MS) return;
  pending.add(key);
  armUnloadListeners();
  flushTimer ??= setTimeout(flushLaunchDarklyEvaluationReports, FLUSH_DELAY_MS);
};

const sendReport = (keys: string[]): void => {
  void fetch(`${getDomains().baseV2Url}feature-control/evaluation-reports`, {
    method: 'POST',
    headers: { ...getV2StreamHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ keys }),
    keepalive: true,
    signal:
      typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
        : undefined,
  }).catch(() => undefined);
};

export function flushLaunchDarklyEvaluationReports(): void {
  if (flushTimer !== undefined) clearTimeout(flushTimer);
  flushTimer = undefined;
  if (pending.size === 0) return;
  try {
    if (isKilled()) {
      pending.clear();
      return;
    }
    const keys = [...pending];
    pending.clear();
    const now = Date.now();
    for (const [key, sentAt] of lastSentAt) {
      if (now - sentAt >= REPORT_INTERVAL_MS) lastSentAt.delete(key);
    }
    for (const key of keys) lastSentAt.set(key, now);
    for (let i = 0; i < keys.length; i += MAX_KEYS_PER_REPORT) {
      sendReport(keys.slice(i, i + MAX_KEYS_PER_REPORT));
    }
  } catch {
    // Never break a flag read or page unload.
  }
}

export function createLaunchDarklyEvaluationReportHook(): Hook {
  return {
    after(hookContext, evaluationDetails) {
      // TELEMETRY-GATE: the sink is a cloud ml-scribe endpoint.
      if (!isSaasTelemetryEnabled) return;
      try {
        if (hookContext.flagKey === EVALUATION_REPORTS_KILL_FLAG) return;
        if (!isServedByLaunchDarkly(evaluationDetails.flagMetadata?.provider)) return;
        recordLaunchDarklyServedRead(hookContext.flagKey);
      } catch {
        // Never break a flag read.
      }
    },
  };
}

export const __resetLaunchDarklyEvaluationReportsForTests = (): void => {
  if (flushTimer !== undefined) clearTimeout(flushTimer);
  flushTimer = undefined;
  pending.clear();
  lastSentAt.clear();
};
